import { workspaceProfilePaths } from "./workspaceProfilePaths";
import { useEffect } from "react";

import { PROFILE_SOURCE_KEY, profileMessage } from "./profileCatalog";
import { useWorkspaceCopy } from "./workspaceCatalog";

const SAFE_TEXT_SELECTOR = [
  ".admin-v12-profile-heading h1", ".admin-v12-profile-heading p", ".admin-v12-profile-heading button",
  ".admin-v12-profile-layout h2", ".admin-v12-profile-layout h3", ".admin-v12-profile-layout label",
  ".admin-v12-profile-layout legend", ".admin-v12-profile-layout dt", ".admin-v12-profile-layout dd",
  ".admin-v12-profile-layout button", ".admin-v12-profile-helper", ".admin-v12-profile-layout option",
  ".admin-v12-avatar-dialog h2", ".admin-v12-avatar-dialog p", ".admin-v12-avatar-dialog button",
  ".admin-v12-avatar-dialog strong", ".admin-v12-avatar-dialog small",
  "#profile-language-region-launcher-slot button",
].join(",");
const SAFE_ATTRIBUTE_SELECTOR = [
  ".admin-v12-profile-layout [aria-label]", ".admin-v12-profile-layout [placeholder]",
  ".admin-v12-avatar-dialog [aria-label]", ".admin-v12-avatar-dialog [alt]",
].join(",");
const SKIP_SELECTOR = "[role='listbox'],code,pre,textarea,[contenteditable='true'],[data-authoritative-content],[data-profile-localized]";

const originalText = new WeakMap<Text, string>();
const renderedText = new WeakMap<Text, string>();
const renderedAttributes = new WeakMap<Element, Map<string, string>>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();

function isProfilePath(path: string): boolean {
  return workspaceProfilePaths(path)?.isProfile ?? false;
}

function replacePreservingWhitespace(source: string, value: string): string {
  const leading = source.match(/^\s*/)?.[0] ?? "";
  const trailing = source.match(/\s*$/)?.[0] ?? "";
  return `${leading}${value}${trailing}`;
}

function translateText(element: Element, locale: ReturnType<typeof useWorkspaceCopy>["locale"]): void {
  if (element.closest(SKIP_SELECTOR)) return;
  element.childNodes.forEach((node) => {
    if (node.nodeType !== Node.TEXT_NODE) return;
    const text = node as Text;
    let source = originalText.get(text);
    const current = text.nodeValue ?? "";
    // React/server state changed the text; an earlier localization is no longer authority.
    if (source !== undefined && current !== renderedText.get(text) && current !== source) {
      originalText.delete(text); source = undefined;
    }
    if (source === undefined) {
      if (!PROFILE_SOURCE_KEY[current.trim()]) return;
      source = current;
      originalText.set(text, source);
    }
    const key = PROFILE_SOURCE_KEY[source.trim()];
    if (!key) return;
    const next = replacePreservingWhitespace(source, profileMessage(locale, key));
    if (text.nodeValue !== next) text.nodeValue = next;
    renderedText.set(text, next);
  });
}

function translateAttributes(element: Element, locale: ReturnType<typeof useWorkspaceCopy>["locale"]): void {
  if (element.closest(SKIP_SELECTOR)) return;
  const originals = originalAttributes.get(element) ?? new Map<string, string>();
  const rendered = renderedAttributes.get(element) ?? new Map<string, string>();
  for (const attribute of ["aria-label", "placeholder", "alt"]) {
    const current = element.getAttribute(attribute);
    if (!current) continue;
    if (originals.has(attribute) && current !== rendered.get(attribute) && current !== originals.get(attribute)) originals.delete(attribute);
    if (!originals.has(attribute) && PROFILE_SOURCE_KEY[current.trim()]) originals.set(attribute, current);
    const source = originals.get(attribute);
    if (!source) continue;
    const key = PROFILE_SOURCE_KEY[source.trim()];
    if (!key) continue;
    const next = profileMessage(locale, key);
    if (element.getAttribute(attribute) !== next) element.setAttribute(attribute, next);
    rendered.set(attribute, next);
  }
  originalAttributes.set(element, originals);
  renderedAttributes.set(element, rendered);
}

export function applyProfileLocalization(root: ParentNode, locale: ReturnType<typeof useWorkspaceCopy>["locale"], path: string): void {
  if (!isProfilePath(path)) return;
  root.querySelectorAll(SAFE_TEXT_SELECTOR).forEach((element) => translateText(element, locale));
  root.querySelectorAll(SAFE_ATTRIBUTE_SELECTOR).forEach((element) => translateAttributes(element, locale));
}

export function ProfileLocalizationRuntime({ path }: { path: string }) {
  const { locale } = useWorkspaceCopy();
  useEffect(() => {
    if (!isProfilePath(path)) return;
    let scheduled = false;
    const apply = () => { scheduled = false; applyProfileLocalization(document, locale, path); };
    apply();
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(apply);
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["aria-label", "placeholder", "alt"] });
    return () => observer.disconnect();
  }, [locale, path]);
  return null;
}
